// Shared days (Planning/SharedDay.md). One person's bookings on one or more
// gigs are priced as ONE person-day: the union of their calls (estimate) or
// clock entries (actual) goes through the same cost engine as everything else,
// so the day rate is charged once and overtime/rest/holiday/rounding see the
// combined hours. The day's cost is then split back to each member booking by
// hours so every gig's budget and payroll still show its own share.
import type { Booking, Call, Position, RateCard, RateEntry, TimesheetEntry } from './types.js';
import type { OTCostOutput, StandardCostOutput } from './ot-cost.js';
import type { Holiday } from './standard-timekeeping.js';
import { costForBookingByDay } from './budget.js';
import { costActualsByDay } from './actuals.js';
import { dateKey } from './quote.js';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export type DayOutput = OTCostOutput | StandardCostOutput;

// bookingId → calendar day (org/venue cost zone) → that booking's share of the
// day, in the same shape the per-booking engine returns, so callers substitute
// it for a booking's own per-day costs without further changes.
export type SharedAllocation = Map<string, Map<string, DayOutput>>;

// Optional shared-day pricing handed to the cost functions. Each field is only
// needed by the callers that use it: `estimate` by offers/quotes/budget,
// `approved` and `projected` by actual costing and payroll.
export interface SharedCosts {
  estimate?: SharedAllocation;
  approved?: SharedAllocation;
  projected?: SharedAllocation;
}

export const sharedTotal = (alloc: SharedAllocation, bookingId: string): number =>
  round2([...(alloc.get(bookingId)?.values() ?? [])].reduce((t, o) => t + o.totalCost, 0));

// Scales every numeric field of a day output (hours and costs) by `share`,
// keeping flags such as holiday / insufficientRest as they were.
function scaleOutput(out: DayOutput, share: number): DayOutput {
  const scaled: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(out)) scaled[k] = typeof v === 'number' && k !== 'tier' ? v * share : v;
  return scaled as unknown as DayOutput;
}

// Splits each day's cost across the members by weight (their hours that day).
// Members with no hours that day get nothing for it. Cents are reconciled: the
// last member with weight absorbs the rounding, so the parts sum to the total
// exactly. If a day has no weights at all (shouldn't happen), the first member
// takes it, so no cost is ever dropped.
export function allocateDayCosts(byDay: Map<string, DayOutput>, weights: Map<string, Map<string, number>>): SharedAllocation {
  const result: SharedAllocation = new Map([...weights.keys()].map(id => [id, new Map<string, DayOutput>()]));
  const memberIds = [...weights.keys()];
  for (const [day, out] of byDay) {
    const present = memberIds.filter(id => (weights.get(id)?.get(day) ?? 0) > 0);
    const among = present.length > 0 ? present : memberIds.slice(0, 1);
    const totalWeight = among.reduce((t, id) => t + (weights.get(id)?.get(day) ?? 0), 0);
    let remaining = round2(out.totalCost);
    among.forEach((id, i) => {
      const share = totalWeight > 0 ? (weights.get(id)?.get(day) ?? 0) / totalWeight : 1;
      const last = i === among.length - 1;
      const cost = last ? remaining : round2(out.totalCost * share);
      remaining = round2(remaining - cost);
      const scaled = scaleOutput(out, share);
      result.get(id)!.set(day, { ...scaled, totalCost: cost } as DayOutput);
    });
  }
  return result;
}

export interface SharedPricing {
  primaryBookingId: string;
  rate: RateEntry;
  rateType: 'day' | 'hourly';
}

type PositionLike = Pick<Position, 'rate' | 'rateType' | 'rateCardId' | 'rateCardRole'>;

export interface SharedEstimateMember {
  booking: Booking;
  position: PositionLike;
  // This member's calls (any superset of booking.callIds is fine).
  calls: Call[];
}

// The synthetic booking/position the union is priced as: the primary's rate-card
// role supplies the rules, the shared day supplies the one rate and rate type.
function pricedAs(primary: { booking: Booking; position: PositionLike }, pricing: SharedPricing, callIds: string[]) {
  return {
    booking: { ...primary.booking, id: `shared:${primary.booking.id}`, callIds, rate: pricing.rate, rateType: pricing.rateType } as Booking,
    position: { ...primary.position, rate: pricing.rate, rateType: pricing.rateType },
  };
}

// ESTIMATE: prices the union of the members' scheduled calls as one person-day
// and splits each day's cost by scheduled hours.
export function estimateSharedDay(
  members: SharedEstimateMember[],
  pricing: SharedPricing,
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
): SharedAllocation {
  if (members.length === 0) return new Map();
  const primary = members.find(m => m.booking.id === pricing.primaryBookingId) ?? members[0];

  const union: Call[] = [];
  const weights = new Map<string, Map<string, number>>();
  for (const m of members) {
    const byDay = new Map<string, number>();
    weights.set(m.booking.id, byDay);
    const own = new Set(m.booking.callIds);
    for (const call of m.calls) {
      if (!own.has(call.id)) continue;
      union.push({ ...call, id: `${m.booking.id}:${call.id}` });
      const day = dateKey(call.startTime, timeZone);
      byDay.set(day, (byDay.get(day) ?? 0) + (call.endTime.getTime() - call.startTime.getTime()) / 3600000);
    }
  }
  const { booking, position } = pricedAs(primary, pricing, union.map(c => c.id));
  const byDay = costForBookingByDay(booking, position, union, rateCards, timeZone, holidays, 'estimate');
  return allocateDayCosts(byDay, weights);
}

export interface SharedActualMember {
  booking: Booking;
  position: PositionLike;
  // The entries to count (the caller filters by status: approved, or approved+pending).
  entries: TimesheetEntry[];
}

// ACTUAL: pools every member's clock entries as one person-day (the day rate
// rounds once on the day's total; hourly periods round individually) and splits
// each day's cost by worked hours.
export function actualSharedDay(
  members: SharedActualMember[],
  pricing: SharedPricing,
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
): SharedAllocation {
  if (members.length === 0) return new Map();
  const primary = members.find(m => m.booking.id === pricing.primaryBookingId) ?? members[0];

  const all: TimesheetEntry[] = [];
  const weights = new Map<string, Map<string, number>>();
  for (const m of members) {
    const byDay = new Map<string, number>();
    weights.set(m.booking.id, byDay);
    for (const e of m.entries) {
      if (!e.clockOut) continue;
      all.push(e);
      const day = dateKey(e.clockIn, timeZone);
      const worked = Math.max(0, e.clockOut.getTime() - e.clockIn.getTime() - (e.breakMinutes ?? 0) * 60000) / 3600000;
      byDay.set(day, (byDay.get(day) ?? 0) + worked);
    }
  }
  if (all.length === 0) return allocateDayCosts(new Map(), weights);
  const { booking, position } = pricedAs(primary, pricing, []);
  const { byDay } = costActualsByDay(booking, position, all, rateCards, timeZone, holidays);
  return allocateDayCosts(byDay, weights);
}

// ---- same-day detection (the assignment prompt) --------------------------------

export interface SpanLike {
  startTime: Date;
  endTime: Date;
}

const ACTIVE_FOR_SHARING = new Set(['DRAFT', 'OFFERED', 'ACCEPTED', 'BOOKED']);

// The person's OTHER active bookings that have a call on the same calendar day
// (cost zone) as the candidate's calls — the ones worth offering to link.
export function findSameDayBookings(
  candidateSpans: SpanLike[],
  others: Array<{ booking: Booking; spans: SpanLike[] }>,
  timeZone = 'UTC',
): Booking[] {
  const days = new Set(candidateSpans.map(s => dateKey(s.startTime, timeZone)));
  if (days.size === 0) return [];
  return others
    .filter(o => ACTIVE_FOR_SHARING.has(o.booking.state) && !o.booking.sharedDayId)
    .filter(o => o.spans.some(s => days.has(dateKey(s.startTime, timeZone))))
    .map(o => o.booking);
}

// ---- link validation -------------------------------------------------------------

// Returns an error message, or null when these bookings may be linked. Linking
// is only allowed BEFORE an offer goes out: the combined offer is the person's
// agreement to be paid as one day, so it can't change after the fact.
export function validateSharedDayLink(members: Booking[], rate: RateEntry | undefined): string | null {
  if (members.length < 2) return 'A shared day needs at least two bookings.';
  if (new Set(members.map(b => b.id)).size !== members.length) return 'The same booking was selected twice.';
  if (new Set(members.map(b => b.orgId)).size !== 1) return 'Bookings must be in the same organization.';
  if (new Set(members.map(b => b.personId)).size !== 1) return 'A shared day links ONE person’s bookings — these are for different people.';
  if (members.some(b => b.sharedDayId)) return 'One of these bookings is already part of a shared day.';
  if (members.some(b => b.state !== 'DRAFT')) {
    return 'Bookings can only be linked before an offer is sent. Cancel the sent offer and re-offer them together instead.';
  }
  if (members.some(b => b.callIds.length === 0)) {
    return 'Every booking needs at least one call to be pooled — a day-scoped booking with no calls can’t be shared.';
  }
  if (!rate || !(rate.amount > 0)) return 'The shared day needs a rate greater than $0.';
  return null;
}

// ---- everything a page needs, in one call -----------------------------------------

export interface SharedMemberData {
  booking: Booking;
  position: PositionLike;
  calls: Call[];
  entries: TimesheetEntry[];
}

// Builds the three shared-day pricings a gig page needs — the schedule estimate,
// the approved actuals, and approved-plus-pending — for every shared day, from
// each member's data. Members whose data isn't loaded are skipped (that shared
// day is then left to price its own members individually until it loads).
export function buildSharedCosts(
  days: Array<{ id: string; members: Array<{ bookingId: string }>; primaryBookingId: string; rate: RateEntry; rateType: 'day' | 'hourly' }>,
  memberData: Map<string, SharedMemberData>,
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
): { estimate: SharedAllocation; approved: SharedAllocation; projected: SharedAllocation } {
  const estimate: SharedAllocation = new Map();
  const approved: SharedAllocation = new Map();
  const projected: SharedAllocation = new Map();
  for (const day of days) {
    const members = day.members.map(m => memberData.get(m.bookingId));
    if (members.some(m => !m)) continue;
    const data = members as SharedMemberData[];
    const pricing = { primaryBookingId: day.primaryBookingId, rate: day.rate, rateType: day.rateType };
    const merge = (target: SharedAllocation, source: SharedAllocation) => source.forEach((v, k) => target.set(k, v));
    merge(estimate, estimateSharedDay(data.map(m => ({ booking: m.booking, position: m.position, calls: m.calls })), pricing, rateCards, timeZone, holidays));
    merge(approved, actualSharedDay(data.map(m => ({ booking: m.booking, position: m.position, entries: m.entries.filter(e => e.bookingId === m.booking.id && e.status === 'APPROVED') })), pricing, rateCards, timeZone, holidays));
    merge(projected, actualSharedDay(data.map(m => ({ booking: m.booking, position: m.position, entries: m.entries.filter(e => e.bookingId === m.booking.id && (e.status === 'APPROVED' || e.status === 'PENDING')) })), pricing, rateCards, timeZone, holidays));
  }
  return { estimate, approved, projected };
}
